import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import { getUserWalletAnalytics, formatETH, formatUSD, getTimeAgo } from '../services/walletAnalyticsService';
import AdminLayout from '../components/AdminLayout';
import Loader from '../components/Loader';
import '../components/AdminLayout.css';

const fullDate = (v) =>
    v ? new Date(v).toLocaleString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) : '';

const shortDate = (v) =>
    v ? new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const STATUS_BADGE = { active: 'badge-success', banned: 'badge-danger', blocked: 'badge-warning' };

const getRoleName = (roleId) => (roleId === 4 ? 'Admin' : 'User');
const getRoleBadgeClass = (roleId) => (roleId === 4 ? 'badge-danger' : 'badge-neutral');

function AdminUserDetail() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentAdmin } = useAuth();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [user, setUser] = useState(null);
    const [authMeta, setAuthMeta] = useState(null);
    const [walletRow, setWalletRow] = useState(null);
    const [walletData, setWalletData] = useState(null);
    const [adminStats, setAdminStats] = useState(null);
    const [tokenBalances, setTokenBalances] = useState([]);
    const [referralInfo, setReferralInfo] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedUser, setEditedUser] = useState({});
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [copied, setCopied] = useState('');

    const [showCollectModal, setShowCollectModal] = useState(false);
    const [collecting, setCollecting] = useState(false);
    const [collectForm, setCollectForm] = useState({ tokenSymbol: 'ETH', amount: '', destinationAddress: '' });
    const [collectResult, setCollectResult] = useState(null);

    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
    };

    const copyToClipboard = (value, key) => {
        if (!value) return;
        navigator.clipboard?.writeText(value)
            .then(() => { setCopied(key); setTimeout(() => setCopied(''), 2000); })
            .catch(() => { /* clipboard unavailable */ });
    };

    const fetchUserDetails = useCallback(async () => {
        try {
            const { data, error } = await supabase.from('users').select('*').eq('id', userId).single();
            if (error) throw error;
            setUser(data);
            setEditedUser(data);
        } catch (error) {
            console.error('Error fetching user details:', error);
            showNotification('Failed to load user details', 'error');
        } finally {
            setLoading(false);
        }
    }, [userId]);

    // public.users.last_login is NULL for every account -- nothing writes it, so
    // this panel used to report "Never signed in" for everyone. The real values
    // live in auth.users, which PostgREST cannot reach; get_user_auth_meta is a
    // SECURITY DEFINER admin-only reader for exactly that. It also returns
    // email_confirmed_at, because whether a user verified is what gates their
    // referrer's bonus.
    const fetchAuthMeta = useCallback(async () => {
        const { data, error } = await supabase.rpc('get_user_auth_meta', { p_user_id: userId });
        if (!error && data && data.length) setAuthMeta(data[0]);
    }, [userId]);

    const fetchWalletData = useCallback(async () => {
        const result = await getUserWalletAnalytics(userId);
        if (result.success) setWalletData(result.data);
        // provisioned_at is not in the analytics RPC, so read the wallet row too.
        // Without it "no wallet" and "wallet with no usable key" look identical.
        const { data } = await supabase
            .from('user_wallets')
            .select('wallet_address, provisioned_at, balance_eth, balance_usd, created_at')
            .eq('user_id', userId)
            .maybeSingle();
        setWalletRow(data || null);
    }, [userId]);

    const fetchTokenBalances = useCallback(async () => {
        const { data, error } = await supabase.rpc('get_user_token_balances', { p_user_id: userId });
        if (!error && data) setTokenBalances(data);
    }, [userId]);

    const fetchReferralInfo = useCallback(async () => {
        try {
            const [signupsRes, bonusRes] = await Promise.all([
                supabase.from('users').select('id, full_name, email, created_at')
                    .eq('referred_by', userId).order('created_at', { ascending: false }),
                supabase.from('wallet_transactions').select('amount_usd, created_at')
                    .eq('to_user_id', userId).eq('transaction_type', 'referral_bonus')
                    .eq('status', 'confirmed').order('created_at', { ascending: false }),
            ]);
            const signups = signupsRes.data || [];
            const bonuses = bonusRes.data || [];
            setReferralInfo({
                signups,
                bonusCount: bonuses.length,
                totalEarned: bonuses.reduce((sum, tx) => sum + Number(tx.amount_usd || 0), 0),
                lastSignupAt: signups[0]?.created_at || null,
                lastBonusAt: bonuses[0]?.created_at || null,
            });
        } catch (error) {
            console.error('Error fetching referral info:', error);
        }
    }, [userId]);

    const fetchAdminStats = useCallback(async () => {
        try {
            const base = () => supabase.from('user_action_logs').select('*', { count: 'exact', head: true }).eq('admin_id', userId);
            const [total, blocked, banned, resets] = await Promise.all([
                base(), base().eq('action', 'blocked'), base().eq('action', 'banned'), base().eq('action', 'password_reset'),
            ]);
            setAdminStats({
                totalActions: total.count || 0,
                usersBlocked: blocked.count || 0,
                usersBanned: banned.count || 0,
                passwordResets: resets.count || 0,
            });
        } catch (error) {
            console.error('Error fetching admin stats:', error);
        }
    }, [userId]);

    useEffect(() => {
        const userRole = currentAdmin?.user_metadata?.role || localStorage.getItem('role');
        if (String(userRole) !== '4') { navigate('/welcome'); return; }
        fetchUserDetails();
        fetchAuthMeta();
        fetchWalletData();
        fetchTokenBalances();
        fetchReferralInfo();
    }, [userId, currentAdmin, navigate, fetchUserDetails, fetchAuthMeta, fetchWalletData, fetchTokenBalances, fetchReferralInfo]);

    useEffect(() => {
        if (user?.role_id === 4) fetchAdminStats();
    }, [user, fetchAdminStats]);

    const handleSaveChanges = async () => {
        setSaving(true);
        try {
            // Email is deliberately NOT written. It lives in auth.users as the login
            // identity; writing it here only to public.users made the two diverge.
            const { error } = await supabase
                .from('users')
                .update({ full_name: editedUser.full_name, phone: editedUser.phone })
                .eq('id', userId);
            if (error) throw error;
            setIsEditing(false);
            await fetchUserDetails();   // re-read rather than trust local state
            showNotification('Profile updated successfully');
        } catch (error) {
            console.error('Error updating user:', error);
            showNotification('Failed to update profile', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleBanUser = async () => {
        if (!window.confirm(`Ban user ${user.email}? They will lose access to their account.`)) return;
        try {
            const { error } = await supabase.from('users').update({
                account_status: 'banned',
                status_reason: 'Banned by admin',
                status_changed_at: new Date().toISOString(),
                status_changed_by: currentAdmin.id,
            }).eq('id', userId);
            if (error) throw error;
            await supabase.from('user_action_logs').insert({
                user_id: userId, admin_id: currentAdmin.id, action: 'banned', reason: 'Banned by admin',
            });
            showNotification('User has been banned');
            fetchUserDetails();
        } catch (error) {
            console.error('Error banning user:', error);
            showNotification('Failed to ban user', 'error');
        }
    };

    const handleRestoreAccess = async () => {
        if (!window.confirm(`Restore access for ${user.email}?`)) return;
        try {
            const { error } = await supabase.from('users').update({
                account_status: 'active',
                status_reason: null,
                status_changed_at: new Date().toISOString(),
                status_changed_by: currentAdmin.id,
            }).eq('id', userId);
            if (error) throw error;
            await supabase.from('user_action_logs').insert({
                user_id: userId,
                admin_id: currentAdmin.id,
                action: user.account_status === 'blocked' ? 'unblocked' : 'unbanned',
                reason: 'Admin restored access',
            });
            showNotification('Account access restored');
            fetchUserDetails();
        } catch (error) {
            console.error('Error restoring access:', error);
            showNotification('Failed to restore access', 'error');
        }
    };

    const handleCollect = async () => {
        const { tokenSymbol, amount, destinationAddress } = collectForm;
        if (!tokenSymbol || !amount || !destinationAddress) {
            showNotification('All fields are required', 'error');
            return;
        }
        if (!/^0x[0-9a-fA-F]{40}$/.test(destinationAddress)) {
            showNotification('Invalid destination address', 'error');
            return;
        }
        if (!(parseFloat(amount) > 0)) {
            showNotification('Amount must be greater than zero', 'error');
            return;
        }

        setCollecting(true);
        setCollectResult(null);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            const jwt = session?.access_token;
            if (!jwt) throw new Error('Not authenticated');

            const res = await fetch('/api/admin-collect-funds', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
                body: JSON.stringify({
                    targetUserId: userId, tokenSymbol, amount: parseFloat(amount), destinationAddress,
                }),
            });
            const result = await res.json();
            if (!res.ok || !result.success) throw new Error(result.error || 'Collection failed');

            setCollectResult({ success: true, txHash: result.txHash, amountUsd: result.amountUsd });
            showNotification(`${tokenSymbol} collected successfully`);
            fetchTokenBalances();
            fetchWalletData();
            setCollectForm((f) => ({ ...f, amount: '', destinationAddress: '' }));
        } catch (err) {
            console.error('Collect error:', err);
            setCollectResult({ success: false, error: err.message });
            showNotification(err.message || 'Collection failed', 'error');
        } finally {
            setCollecting(false);
        }
    };

    const closeCollectModal = useCallback(() => {
        if (collecting) return;
        setShowCollectModal(false);
    }, [collecting]);

    useEffect(() => {
        if (!showCollectModal) return;
        const onKey = (e) => { if (e.key === 'Escape') closeCollectModal(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showCollectModal, closeCollectModal]);

    if (loading) return <AdminLayout><Loader /></AdminLayout>;

    if (!user) {
        return (
            <AdminLayout>
                <div className="admin-card">
                    <div className="admin-empty">
                        <i className="bx bx-error-circle"></i>
                        <p>No user found for this ID.</p>
                        <button className="btn btn-secondary" onClick={() => navigate('/admin/users')}>
                            <i className="bx bx-arrow-back"></i>
                            Back to Users
                        </button>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    const status = String(user.account_status || 'active').toLowerCase();
    const isSuspended = status === 'banned' || status === 'blocked';
    const displayName = user.full_name || user.email || 'Unknown user';
    const avatarSrc = user.profile_image || user.avatar_url || null;
    const walletAddress = walletRow?.wallet_address || walletData?.wallet_address || null;
    const isProvisioned = Boolean(walletRow?.provisioned_at && walletAddress);
    const usdBalance = walletData?.fiat_balance_usd ?? walletRow?.balance_usd ?? 0;
    const ethBalance = walletData?.crypto_balance_eth ?? walletRow?.balance_eth ?? 0;

    const CopyBtn = ({ value, name }) => (
        <button className="link-button" onClick={() => copyToClipboard(value, name)}
                title="Copy" aria-label={`Copy ${name}`}>
            <i className={`bx ${copied === name ? 'bx-check' : 'bx-copy'}`}></i>
        </button>
    );

    return (
        <AdminLayout>
            {notification.show && (
                <div className={`admin-alert ${notification.type === 'error' ? 'admin-alert-danger' : 'admin-alert-success'}`}>
                    <span className="admin-alert-message">
                        <i className={`bx ${notification.type === 'error' ? 'bx-error-circle' : 'bx-check-circle'}`}></i>
                        {notification.message}
                    </span>
                </div>
            )}

            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        <i className="bx bx-user-circle"></i>
                        User Profile
                    </h1>
                    <p className="page-subtitle">{displayName}</p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {!isEditing ? (
                        <button className="btn btn-primary" onClick={() => setIsEditing(true)}>
                            <i className="bx bx-edit"></i>
                            Edit Profile
                        </button>
                    ) : (
                        <>
                            <button className="btn btn-secondary"
                                    onClick={() => { setIsEditing(false); setEditedUser(user); }}>
                                <i className="bx bx-x"></i>
                                Cancel
                            </button>
                            <button className="btn btn-primary" onClick={handleSaveChanges} disabled={saving}>
                                <i className="bx bx-check"></i>
                                {saving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </>
                    )}
                    <button className="btn btn-secondary" onClick={() => navigate('/admin/users')}>
                        <i className="bx bx-arrow-back"></i>
                        Back to Users
                    </button>
                </div>
            </div>

            <div className="profile-layout">
                {/* ── Identity ─────────────────────────────────────────── */}
                <div>
                    <div className="admin-card identity-card">
                        <div className="identity-avatar">
                            {avatarSrc
                                ? <img src={avatarSrc} alt={displayName} />
                                : displayName.charAt(0).toUpperCase()}
                        </div>
                        <h2 className="identity-name">{user.full_name || 'No name set'}</h2>
                        <p className="identity-email">{user.email}</p>
                        <div className="identity-badges">
                            <span className={`badge ${getRoleBadgeClass(user.role_id)}`}>
                                {getRoleName(user.role_id)}
                            </span>
                            <span className={`badge ${STATUS_BADGE[status] || 'badge-neutral'}`}>
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                            </span>
                        </div>

                        <div className="identity-meta">
                            <div className="detail-label">User ID</div>
                            <div className="copy-row">
                                <code className="code-chip" title={user.id}>{user.id}</code>
                                <CopyBtn value={user.id} name="user ID" />
                            </div>
                        </div>
                        <div className="identity-meta">
                            <div className="detail-label">Member Since</div>
                            <div className="detail-value" title={fullDate(user.created_at)}>
                                {shortDate(user.created_at)}
                            </div>
                        </div>
                        <div className="identity-meta">
                            <div className="detail-label">Last Login</div>
                            <div className={`detail-value${authMeta?.last_sign_in_at ? '' : ' muted'}`}
                                 title={fullDate(authMeta?.last_sign_in_at)}>
                                {authMeta?.last_sign_in_at ? shortDate(authMeta.last_sign_in_at) : 'Never signed in'}
                            </div>
                        </div>
                        <div className="identity-meta">
                            <div className="detail-label">Email Verified</div>
                            <div className={`detail-value${authMeta?.email_confirmed_at ? '' : ' muted'}`}
                                 title={fullDate(authMeta?.email_confirmed_at)}>
                                {authMeta?.email_confirmed_at ? shortDate(authMeta.email_confirmed_at) : 'Not verified'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Balance, details, holdings ───────────────────────── */}
                <div>
                    <div className="admin-card">
                        <div className="card-header card-header-wrap">
                            <h3 className="card-title">
                                <i className="bx bx-wallet"></i>
                                Balance &amp; Wallet
                            </h3>
                            {user.role_id !== 4 && isProvisioned && (
                                <div className="card-toolbar">
                                    <button className="btn btn-sm btn-secondary"
                                            onClick={() => { setShowCollectModal(true); setCollectResult(null); }}>
                                        <i className="bx bx-download"></i>
                                        Collect Funds
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="stats-grid stats-grid-compact">
                            <div className="stat-card">
                                <div className="stat-icon success"><i className="bx bx-dollar-circle"></i></div>
                                <div className="stat-content">
                                    <div className="stat-label">Spendable Balance</div>
                                    <div className="stat-value">{formatUSD(usdBalance)}</div>
                                    <div className="stat-change">off-chain, instant</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon primary"><i className="bx bx-coin-stack"></i></div>
                                <div className="stat-content">
                                    <div className="stat-label">ETH Balance</div>
                                    <div className="stat-value">{formatETH(ethBalance)}</div>
                                    <div className="stat-change">on-chain deposits</div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon info"><i className="bx bx-transfer"></i></div>
                                <div className="stat-content">
                                    <div className="stat-label">Transactions</div>
                                    <div className="stat-value">{walletData?.total_transactions ?? 0}</div>
                                    <div className="stat-change">
                                        {walletData?.last_transaction_date
                                            ? `last ${getTimeAgo(walletData.last_transaction_date)}`
                                            : 'no activity yet'}
                                    </div>
                                </div>
                            </div>
                            <div className="stat-card">
                                <div className="stat-icon warning"><i className="bx bx-calendar"></i></div>
                                <div className="stat-content">
                                    <div className="stat-label">Today's Activity</div>
                                    <div className="stat-value">{walletData?.transactions_today ?? 0}</div>
                                    <div className="stat-change">transactions today</div>
                                </div>
                            </div>
                        </div>

                        <div className="detail-grid" style={{ marginTop: '1.25rem' }}>
                            <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                                <div className="detail-label">
                                    <i className="bx bx-chip"></i>
                                    Deposit Address
                                </div>
                                {isProvisioned ? (
                                    <div className="copy-row">
                                        <code className="code-chip" title={walletAddress}>{walletAddress}</code>
                                        <CopyBtn value={walletAddress} name="wallet address" />
                                        <a className="link-button" href={`https://etherscan.io/address/${walletAddress}`}
                                           target="_blank" rel="noopener noreferrer" title="View on Etherscan">
                                            <i className="bx bx-link-external"></i>
                                        </a>
                                    </div>
                                ) : (
                                    <div className="detail-value muted">
                                        <span className="badge badge-warning">Awaiting provisioning</span>
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
                                            No keypair has been minted for this wallet yet, so it has no
                                            usable address. It is created on the user's next sign-in.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Account details ──────────────────────────────── */}
                    <div className="admin-card">
                        <div className="card-header">
                            <h3 className="card-title">
                                <i className="bx bx-id-card"></i>
                                Account Details
                            </h3>
                        </div>
                        <div className="detail-grid">
                            <div className="detail-item">
                                <div className="detail-label"><i className="bx bx-user"></i> Full Name</div>
                                {isEditing ? (
                                    <input type="text" className="form-input" value={editedUser.full_name || ''}
                                           onChange={(e) => setEditedUser({ ...editedUser, full_name: e.target.value })} />
                                ) : (
                                    <div className={`detail-value${user.full_name ? '' : ' muted'}`}>
                                        {user.full_name || 'Not provided'}
                                    </div>
                                )}
                            </div>
                            <div className="detail-item">
                                <div className="detail-label"><i className="bx bx-phone"></i> Phone Number</div>
                                {isEditing ? (
                                    <input type="tel" className="form-input" value={editedUser.phone || ''}
                                           onChange={(e) => setEditedUser({ ...editedUser, phone: e.target.value })} />
                                ) : (
                                    <div className={`detail-value${user.phone ? '' : ' muted'}`}>
                                        {user.phone || 'Not provided'}
                                    </div>
                                )}
                            </div>
                            <div className="detail-item">
                                <div className="detail-label"><i className="bx bx-envelope"></i> Email Address</div>
                                <div className="detail-value">{user.email}</div>
                                {isEditing && (
                                    <div className="form-text">
                                        Read-only — this is the login identity, held in the auth system.
                                    </div>
                                )}
                            </div>
                            <div className="detail-item">
                                <div className="detail-label"><i className="bx bx-shield"></i> Account Type</div>
                                <div className="detail-value">{getRoleName(user.role_id)} account</div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label"><i className="bx bx-gift"></i> Referral Code</div>
                                <div className="copy-row">
                                    <code className="code-chip">{user.referral_code || '—'}</code>
                                    {user.referral_code && <CopyBtn value={user.referral_code} name="referral code" />}
                                </div>
                            </div>
                            <div className="detail-item">
                                <div className="detail-label"><i className="bx bx-user-check"></i> Referred By</div>
                                <div className="detail-value">
                                    {user.referred_by ? (
                                        <button className="link-button"
                                                onClick={() => navigate(`/admin/users/${user.referred_by}`)}>
                                            View referrer
                                        </button>
                                    ) : <span className="detail-value muted">Direct signup</span>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Crypto holdings ──────────────────────────────── */}
                    {user.role_id !== 4 && isProvisioned && (
                        <div className="admin-card">
                            <div className="card-header">
                                <h3 className="card-title">
                                    <i className="bx bx-coin-stack"></i>
                                    Crypto Holdings
                                </h3>
                            </div>
                            {tokenBalances.length === 0 ? (
                                <div className="admin-empty">
                                    <i className="bx bx-coin-stack"></i>
                                    <p>No token balances</p>
                                </div>
                            ) : (
                                <div className="admin-table-wrap">
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                <th>Token</th>
                                                <th className="num">Balance</th>
                                                <th className="num">Total Deposited</th>
                                                <th className="num">Total Collected</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tokenBalances.map((tb) => {
                                                const dp = tb.decimals === 6 ? 4 : 6;
                                                return (
                                                    <tr key={tb.token_symbol}>
                                                        <td>
                                                            <div className="user-cell-text">
                                                                <div className="user-cell-name">{tb.token_symbol}</div>
                                                                <div className="user-cell-sub">{tb.token_name}</div>
                                                            </div>
                                                        </td>
                                                        <td className="num">{Number(tb.balance || 0).toFixed(dp)}</td>
                                                        <td className="num">{Number(tb.total_deposited || 0).toFixed(dp)}</td>
                                                        <td className="num">{Number(tb.total_collected || 0).toFixed(dp)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Referral activity ────────────────────────────────────── */}
            {referralInfo && (
                <div className="admin-card">
                    <div className="card-header card-header-wrap">
                        <h3 className="card-title">
                            <i className="bx bx-gift"></i>
                            Referral Activity
                        </h3>
                        <div className="card-toolbar">
                            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/admin/referrals/${userId}`)}>
                                <i className="bx bx-gift"></i>
                                Referral Details
                            </button>
                            <button className="btn btn-sm btn-secondary" onClick={() => navigate('/admin/referrals')}>
                                <i className="bx bx-list-ul"></i>
                                Leaderboard
                            </button>
                        </div>
                    </div>
                    <div className="stats-grid stats-grid-compact">
                        <div className="stat-card">
                            <div className="stat-icon primary"><i className="bx bx-user-plus"></i></div>
                            <div className="stat-content">
                                <div className="stat-label">Signups Referred</div>
                                <div className="stat-value">{referralInfo.signups.length}</div>
                                {referralInfo.lastSignupAt && (
                                    <div className="stat-change">last {getTimeAgo(referralInfo.lastSignupAt)}</div>
                                )}
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-icon success"><i className="bx bx-dollar-circle"></i></div>
                            <div className="stat-content">
                                <div className="stat-label">Bonuses Earned</div>
                                <div className="stat-value">{formatUSD(referralInfo.totalEarned)}</div>
                                <div className="stat-change">
                                    {referralInfo.bonusCount} payment{referralInfo.bonusCount === 1 ? '' : 's'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {referralInfo.signups.length > 0 && (
                        <div className="admin-table-wrap">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>Referred User</th>
                                        <th>Signed Up</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {referralInfo.signups.map((s) => (
                                        <tr key={s.id}>
                                            <td>
                                                <div className="user-cell">
                                                    <div className="user-avatar-small">
                                                        {(s.full_name || s.email || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="user-cell-text">
                                                        <div className="user-cell-name">{s.full_name || 'N/A'}</div>
                                                        <div className="user-cell-sub" title={s.email}>{s.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td title={fullDate(s.created_at)}>{getTimeAgo(s.created_at)}</td>
                                            <td>
                                                <button className="btn btn-sm btn-secondary"
                                                        onClick={() => navigate(`/admin/users/${s.id}`)}>
                                                    <i className="bx bx-show"></i>
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* ── Admin activity ───────────────────────────────────────── */}
            {user.role_id === 4 && adminStats && (
                <div className="admin-card">
                    <div className="card-header">
                        <h3 className="card-title">
                            <i className="bx bx-shield-quarter"></i>
                            Admin Activity
                        </h3>
                    </div>
                    <div className="stats-grid stats-grid-compact">
                        {[
                            { label: 'Total Actions', value: adminStats.totalActions, icon: 'bx-task', color: 'primary' },
                            { label: 'Users Blocked', value: adminStats.usersBlocked, icon: 'bx-block', color: 'warning' },
                            { label: 'Users Banned', value: adminStats.usersBanned, icon: 'bx-user-x', color: 'danger' },
                            { label: 'Password Resets', value: adminStats.passwordResets, icon: 'bx-key', color: 'info' },
                        ].map((s) => (
                            <div className="stat-card" key={s.label}>
                                <div className={`stat-icon ${s.color}`}><i className={`bx ${s.icon}`}></i></div>
                                <div className="stat-content">
                                    <div className="stat-label">{s.label}</div>
                                    <div className="stat-value">{s.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Account management ───────────────────────────────────── */}
            {user.role_id !== 4 && (
                <div className={`admin-card danger-zone${isSuspended ? ' safe' : ''}`}>
                    <div className="card-header">
                        <h3 className="card-title">
                            <i className={`bx ${isSuspended ? 'bx-lock-open-alt' : 'bx-shield-x'}`}></i>
                            Account Management
                        </h3>
                    </div>

                    {isSuspended ? (
                        <>
                            <div className="status-callout">
                                <strong>{status === 'blocked' ? 'Blocked' : 'Banned'}</strong>
                                {user.status_reason ? ` — ${user.status_reason}` : ''}
                                {user.status_changed_at ? ` (${fullDate(user.status_changed_at)})` : ''}
                            </div>
                            <button className="btn btn-success" onClick={handleRestoreAccess}>
                                <i className="bx bx-check-shield"></i>
                                Restore Access
                            </button>
                            <p className="zone-note">
                                Restores the account to active and lets the user sign in again.
                            </p>
                        </>
                    ) : (
                        <>
                            <button className="btn btn-danger" onClick={handleBanUser}>
                                <i className="bx bx-block"></i>
                                Ban User
                            </button>
                            <p className="zone-note">
                                Immediately revokes access. The user is signed out and shown a suspension
                                notice until an admin restores the account. Their balance is untouched.
                            </p>
                        </>
                    )}
                </div>
            )}

            {/* ── Collect Funds modal ──────────────────────────────────── */}
            {showCollectModal && (
                <div className="admin-modal-overlay" onClick={closeCollectModal} role="presentation">
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}
                         role="dialog" aria-modal="true" aria-label="Collect funds">
                        <div className="admin-modal-header">
                            <h3 className="admin-modal-title">
                                <i className="bx bx-download"></i>
                                Collect Funds
                            </h3>
                            <button className="admin-modal-close" onClick={closeCollectModal} aria-label="Close">×</button>
                        </div>

                        <div className="admin-modal-body">
                            <div className="form-group">
                                <label className="form-label">Token</label>
                                <select className="form-input" value={collectForm.tokenSymbol}
                                        onChange={(e) => setCollectForm({ ...collectForm, tokenSymbol: e.target.value })}>
                                    {tokenBalances.map((tb) => (
                                        <option key={tb.token_symbol} value={tb.token_symbol}>
                                            {tb.token_name} ({tb.token_symbol}) — Balance: {Number(tb.balance || 0).toFixed(6)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Amount</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input type="number" step="any" min="0" className="form-input"
                                           value={collectForm.amount}
                                           onChange={(e) => setCollectForm({ ...collectForm, amount: e.target.value })}
                                           placeholder="0.00" />
                                    <button className="btn btn-secondary" type="button" onClick={() => {
                                        const tb = tokenBalances.find((t) => t.token_symbol === collectForm.tokenSymbol);
                                        if (tb) setCollectForm((f) => ({ ...f, amount: Number(tb.balance || 0).toFixed(6) }));
                                    }}>Max</button>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="form-label">Destination Address</label>
                                <input type="text" className="form-input" value={collectForm.destinationAddress}
                                       onChange={(e) => setCollectForm({ ...collectForm, destinationAddress: e.target.value })}
                                       placeholder="0x…" />
                                <div className="form-text">Funds are sent on Ethereum mainnet and cannot be reversed.</div>
                            </div>

                            {collectResult && (
                                <div className={`admin-alert ${collectResult.success ? 'admin-alert-success' : 'admin-alert-danger'}`}>
                                    <span className="admin-alert-message">
                                        <i className={`bx ${collectResult.success ? 'bx-check-circle' : 'bx-error-circle'}`}></i>
                                        {collectResult.success ? (
                                            <span>
                                                Collected{collectResult.amountUsd ? ` (${formatUSD(collectResult.amountUsd)})` : ''} —{' '}
                                                <a href={`https://etherscan.io/tx/${collectResult.txHash}`}
                                                   target="_blank" rel="noopener noreferrer">view transaction</a>
                                            </span>
                                        ) : collectResult.error}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="admin-modal-footer">
                            <button className="btn btn-secondary" style={{ flex: 1 }}
                                    onClick={closeCollectModal} disabled={collecting}>
                                Cancel
                            </button>
                            <button className="btn btn-primary" style={{ flex: 2 }}
                                    onClick={handleCollect} disabled={collecting}>
                                {collecting ? (<><i className="bx bx-loader-alt bx-spin"></i> Collecting…</>)
                                            : (<><i className="bx bx-check"></i> Confirm Collection</>)}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
}

export default AdminUserDetail;
